import React from 'react';
import type { HeadingLevel } from 'volto-ministerio-publico-federal/types/heading';

export interface MPFBlockCardHeaderProps {
  title: string;
  /** Id do heading, para a seção referenciá-lo em `aria-labelledby`. */
  titleId?: string;
  /**
   * Ícone à esquerda do título, já instanciado e dimensionado pelo chamador
   * (`h-6 w-6 shrink-0`). Decorativo: quem nomeia a seção é o texto.
   */
  icon?: React.ReactNode;
  /**
   * Cor da régua, como classe utilitária (`bg-teal-600`, `bg-amber-400`…).
   * Cada faixa da sub-home tem a sua no design — mas só no tema claro: no
   * alto contraste todas as réguas ficam neutras (`night-750`), por decisão
   * do handoff do tema dark, e a cor da seção passa a viver só no ícone e
   * nas tags. Daí o `dark:!bg-night-750` fixo no elemento abaixo.
   */
  ruleClassName?: string;
  /**
   * Nível do heading. Ver `MPFSectionHeading`: nenhum componente reusável
   * crava a tag, porque a mesma faixa aparece em profundidades diferentes.
   */
  as?: HeadingLevel;
}

/**
 * Cabeçalho das faixas em cartão: ícone + título em `blue-800` e uma régua de
 * 2px na largura do cartão.
 *
 * Não confundir com o `MPFSectionHeading`, que é outro desenho — título cinza
 * com uma linha fina À DIREITA — e continua sendo o cabeçalho da faixa de
 * notícias (`mpfNoticiasPorTema`, `mpfCampanhas`).
 *
 * Devolve um fragmento, e não um `<header>`: os 16px entre título, régua e
 * conteúdo saem do `gap-4` do cartão que envolve tudo (mesma mecânica de
 * `View/CapaView/OndeAtuaSection.tsx`). Um wrapper aqui criaria um contexto
 * de flex próprio e a régua deixaria de acompanhar a largura do cartão.
 */
export const MPFBlockCardHeader: React.FC<MPFBlockCardHeaderProps> = ({
  title,
  titleId,
  icon,
  ruleClassName = 'bg-teal-600',
  as: Heading = 'h2',
}) => {
  if (!title) return null;

  return (
    <>
      <div className="flex w-full items-center gap-2">
        {icon}
        <Heading
          id={titleId}
          className="mb-0 text-reading font-semibold text-blue-800 dark:text-night-50"
        >
          {title}
        </Heading>
      </div>

      {/* Decorativa: o cabeçalho acima já é o rótulo da seção. */}
      <div
        aria-hidden="true"
        className={`h-0.5 w-full shrink-0 dark:!bg-night-750 ${ruleClassName}`}
      />
    </>
  );
};

export default MPFBlockCardHeader;
